import { Link, useLocation, Outlet, Navigate } from "react-router-dom";
import { PageHeader } from "../components/layout/PageHeader";
import { SETTINGS_NAV } from "./settings/sections";
import { useTheme } from "../contexts/ThemeContext";
import { Sun, Moon, Wand2, LogOut } from "lucide-react";
import { cn } from "../lib/utils";

export default function Settings() {
  const location = useLocation();
  // /settings (no sub) → redirect to /settings/profile
  if (location.pathname === "/settings" || location.pathname === "/settings/") {
    return <Navigate to="/settings/profile" replace />;
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      <PageHeader
        kicker="System · Settings"
        title="Account & workspace"
        description="Profile, subscription, usage, API keys, webhooks, team, brand, and privacy — all in one place."
      />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <SettingsSidebar />
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function SettingsSidebar() {
  const { pathname } = useLocation();
  const { theme, cycle } = useTheme();
  const ThemeIcon = theme === "dark" ? Moon : theme === "creator" ? Wand2 : Sun;

  return (
    <aside
      data-testid="settings-sidebar"
      className="rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 p-3 h-fit lg:sticky lg:top-20"
    >
      <div className="px-3 py-3 mb-2 flex items-center gap-3 border-b border-[var(--pl-border)]">
        <div className="h-10 w-10 rounded-xl pl-signature-gradient grid place-items-center text-[#061414] font-bold text-sm">PL</div>
        <div className="leading-tight">
          <div className="text-sm font-medium text-[var(--pl-text)]">modularfounder</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">Personal · Demo</div>
        </div>
      </div>

      <nav className="space-y-4 max-h-[calc(100vh-260px)] overflow-y-auto pl-noscroll pr-1">
        {SETTINGS_NAV.map((sec) => (
          <div key={sec.section}>
            <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
              {sec.section}
            </div>
            <div className="space-y-0.5">
              {sec.items.map((item) => {
                const Active = pathname === item.to || pathname.startsWith(item.to + "/");
                const Icon = item.icon;
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    data-testid={`settings-nav-${item.id}`}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition",
                      Active
                        ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)]"
                        : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)] hover:bg-white/5"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.7} />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="mt-4 pt-3 border-t border-[var(--pl-border)] flex items-center gap-1">
        <button
          data-testid="settings-theme-toggle"
          onClick={cycle}
          className="flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
        >
          <ThemeIcon className="h-3.5 w-3.5" /> {theme}
        </button>
        <button
          data-testid="settings-signout"
          className="h-9 w-9 grid place-items-center rounded-xl border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-rose-400"
          aria-label="Sign out"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </aside>
  );
}
